/**
 * Centralized API Client
 * Unified API service with /api/v1 base URL, proper error handling,
 * authentication via xi-api-key headers, and support for JSON/audio responses
 */

import axios from 'axios';

export class ApiClient {
  constructor(config = {}) {
    // Always use the production API endpoint
    const baseURL = 'https://api.44agents.com/v1';

    this.config = {
      baseURL,
      timeout: 30000,
      retries: 3,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      withCredentials: false,
      ...config,
    };

    this.client = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: this.config.headers,
      withCredentials: this.config.withCredentials,
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor for authentication and logging
    this.client.interceptors.request.use(
      (config) => {
        // Add authentication headers
        const apiKey = localStorage.getItem('PROVIDER_API_KEY');
        if (apiKey) {
          config.headers['xi-api-key'] = apiKey;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling and logging
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;


        // Handle specific error cases
        if (error.response?.status === 401) {
          // 401 Unauthorized - Only handle if we're not already on the login page
          if (!window.location.pathname.includes('/login')) {
            console.error('401 Unauthorized - Clearing authentication and redirecting to login');
            
            // Clear all authentication data
            localStorage.removeItem('PROVIDER_API_KEY');
            localStorage.removeItem('user_info');
            localStorage.removeItem('userName');
            localStorage.removeItem('userEmail');
            localStorage.removeItem('userPhotoURL');
            localStorage.removeItem('userId');
            
            // Sign out from Firebase if authService is available
            try {
              // Dynamically import authService to avoid circular dependencies
              const { authService } = await import('../../services/authService');
              if (authService && authService.signOut) {
                await authService.signOut();
              }
            } catch (signOutError) {
              console.error('Error signing out:', signOutError);
            }
            
            // Redirect to login page
            window.location.href = '/login';
          }
          
          // Return the error to stop further processing
          return Promise.reject({
            code: 'UNAUTHORIZED',
            message: 'Your session has expired. Please log in again.',
            timestamp: new Date().toISOString(),
          });
        }

        // Retry logic for network errors
        if (this.shouldRetry(error) && !originalRequest._retry) {
          originalRequest._retry = true;
          const retryCount = originalRequest._retryCount || 0;
          
          if (retryCount < this.config.retries) {
            originalRequest._retryCount = retryCount + 1;
            const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff
            
            await this.delay(delay);
            return this.client(originalRequest);
          }
        }

        return Promise.reject(this.formatError(error));
      }
    );
  }

  shouldRetry(error) {
    // Retry on network errors or 5xx server errors
    return (
      !error.response ||
      error.code === 'NETWORK_ERROR' ||
      error.code === 'TIMEOUT' ||
      (error.response.status >= 500 && error.response.status < 600)
    );
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  generateRequestId() {
    return `req_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  formatError(error) {
    const apiError = {
      code: error.response?.data?.code || error.code || 'UNKNOWN_ERROR',
      message: error.response?.data?.message || error.message || 'An unknown error occurred',
      timestamp: new Date().toISOString(),
    };

    if (error.response?.data?.details) {
      apiError.details = error.response.data.details;
    }

    return apiError;
  }

  // Generic request methods
  async get(url, options = {}) {
    try {
      const response = await this.client.get(url, {
        params: options.params,
        headers: options.headers,
        timeout: options.timeout,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async post(url, data, options = {}) {
    try {
      const response = await this.client.post(url, data, {
        headers: options.headers,
        timeout: options.timeout,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async put(url, data, options = {}) {
    try {
      const response = await this.client.put(url, data, {
        headers: options.headers,
        timeout: options.timeout,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async patch(url, data, options = {}) {
    try {
      const response = await this.client.patch(url, data, {
        headers: options.headers,
        timeout: options.timeout,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async delete(url, options = {}) {
    try {
      const response = await this.client.delete(url, {
        headers: options.headers,
        timeout: options.timeout,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Special method for audio responses
  async getAudio(url, options = {}) {
    try {
      const response = await this.client.get(url, {
        responseType: 'arraybuffer',
        headers: {
          'Accept': 'audio/mpeg',
          ...options.headers,
        },
        params: options.params,
        timeout: options.timeout,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Method for posting with audio response
  async postAudio(url, data, options = {}) {
    try {
      const response = await this.client.post(url, data, {
        responseType: 'arraybuffer',
        headers: {
          'Accept': 'audio/mpeg',
          'Content-Type': 'application/json',
          ...options.headers,
        },
        params: options.params,
        timeout: options.timeout,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Method for posting with stream response
  async postStream(url, data, options = {}) {
    try {
      const response = await this.client.post(url, data, {
        responseType: 'stream',
        headers: {
          'Accept': 'application/octet-stream',
          'Content-Type': 'application/json',
          ...options.headers,
        },
        params: options.params,
        timeout: options.timeout,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Method for posting multipart/form-data
  async postFormData(url, formData, options = {}) {
    try {
      const response = await this.client.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...options.headers,
        },
        params: options.params,
        timeout: options.timeout || 60000, // Longer timeout for file uploads
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Method for posting form-data with stream response
  async postFormDataStream(url, formData, options = {}) {
    try {
      const response = await this.client.post(url, formData, {
        responseType: 'stream',
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept': 'text/event-stream',
          ...options.headers,
        },
        params: options.params,
        timeout: options.timeout || 60000,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Update API key
  updateApiKey(apiKey) {
    this.config.apiKey = apiKey;
    this.client.defaults.headers['xi-api-key'] = apiKey;
    localStorage.setItem('PROVIDER_API_KEY', apiKey);
  }

  // Get current configuration
  getConfig() {
    return { ...this.config };
  }

  // Update configuration
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    
    // Update axios instance if needed
    if (newConfig.baseURL) {
      this.client.defaults.baseURL = newConfig.baseURL;
    }
    if (newConfig.timeout) {
      this.client.defaults.timeout = newConfig.timeout;
    }
    if (newConfig.headers) {
      Object.assign(this.client.defaults.headers, newConfig.headers);
    }
  }
}

// Create and export a default instance
export const apiClient = new ApiClient();

// Export the class for custom instances
export default ApiClient;
